import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaFacebook, FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa";
import {
  FiArrowUpRight,
  FiCheckCircle,
  FiMessageCircle,
  FiPlay,
  FiUser,
} from "react-icons/fi";

const BULLETS = [
  "Match with verified brands and get clear briefs for reels and stories",
  "Chat, approve drafts, and go live without long email threads",
  "Track views, clicks, and payouts across Instagram, TikTok, YouTube, and Facebook",
];

const SLIDES = [
  {
    id: "chat",
    label: "Campaign Chat",
    title: "Brand ↔ Creator chat",
    hint: "Briefs, drafts, and approvals in one place",
  },
  {
    id: "performance",
    label: "Performance",
    title: "Live campaign results",
    hint: "Views, clicks, sales, and ROAS at a glance",
  },
  {
    id: "earn",
    label: "Create & Earn",
    title: "Post, get paid",
    hint: "See reach, engagement, and estimated earnings",
  },
];

const AUTO_ROTATE_MS = 4500;

const SocialIconRow = ({ className = "" }) => (
  <div className={`flex items-center gap-1.5 sm:gap-2 ${className}`}>
    <span className="grid h-7 w-7 place-items-center rounded-lg bg-orange-50 ring-1 ring-orange-100 sm:h-8 sm:w-8">
      <FaInstagram className="h-3.5 w-3.5 text-orange-500 sm:h-4 sm:w-4" />
    </span>
    <span className="grid h-7 w-7 place-items-center rounded-lg bg-gray-50 ring-1 ring-gray-200 sm:h-8 sm:w-8">
      <FaTiktok className="h-3.5 w-3.5 text-gray-800 sm:h-4 sm:w-4" />
    </span>
    <span className="grid h-7 w-7 place-items-center rounded-lg bg-red-50 ring-1 ring-red-100 sm:h-8 sm:w-8">
      <FaYoutube className="h-3.5 w-3.5 text-red-500 sm:h-4 sm:w-4" />
    </span>
    <span className="grid h-7 w-7 place-items-center rounded-lg bg-blue-50 ring-1 ring-blue-100 sm:h-8 sm:w-8">
      <FaFacebook className="h-3.5 w-3.5 text-blue-500 sm:h-4 sm:w-4" />
    </span>
  </div>
);

const Metric = ({ label, value, tone = "default" }) => {
  const valueClass =
    tone === "orange"
      ? "text-orange-600"
      : tone === "green"
        ? "text-emerald-600"
        : tone === "blue"
          ? "text-blue-600"
          : "text-gray-900";

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-2.5 py-2 text-center sm:px-3 sm:py-2.5">
      <div className={`text-sm font-bold sm:text-base ${valueClass}`}>{value}</div>
      <div className="mt-0.5 text-[10px] text-gray-500 sm:text-xs">{label}</div>
    </div>
  );
};

const ChatPanel = () => (
  <div className="space-y-3 sm:space-y-4">
    <div className="flex items-start gap-2.5">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-orange-50 ring-1 ring-orange-100">
        <FiUser className="h-4 w-4 text-orange-500" />
      </div>
      <div className="min-w-0 flex-1 rounded-2xl rounded-tl-md border border-gray-100 bg-gray-50 px-3 py-2.5">
        <p className="text-xs text-gray-800 sm:text-sm">
          Can you post 1 reel + 2 stories this week?
        </p>
        <p className="mt-1 text-[10px] font-medium text-gray-400 sm:text-xs">Brand manager</p>
      </div>
    </div>

    <div className="flex items-start justify-end gap-2.5">
      <div className="min-w-0 max-w-[85%] rounded-2xl rounded-tr-md border border-orange-100 bg-orange-50 px-3 py-2.5 sm:max-w-[80%]">
        <p className="text-xs text-gray-800 sm:text-sm">
          Done. I&apos;ll shoot today and share a draft.
        </p>
        <p className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold text-orange-600 sm:text-xs">
          <FiCheckCircle className="h-3.5 w-3.5" />
          Reel approved
        </p>
      </div>
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-blue-50 ring-1 ring-blue-100">
        <FiUser className="h-4 w-4 text-blue-500" />
      </div>
    </div>

    <div className="grid grid-cols-3 gap-2">
      <Metric label="Avg. reply" value="3m" tone="orange" />
      <Metric label="On-time" value="98%" />
      <Metric label="Rating" value="4.9★" />
    </div>

    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gray-50 ring-1 ring-gray-200">
          <FiPlay className="h-4 w-4 text-orange-500" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-gray-900 sm:text-sm">Reel brief</p>
          <p className="truncate text-[10px] text-gray-500 sm:text-xs">Hook · Demo · CTA</p>
        </div>
      </div>
      <span className="shrink-0 rounded-full bg-orange-500 px-2.5 py-1 text-[10px] font-bold text-white sm:text-xs">
        ₹ Payout
      </span>
    </div>
  </div>
);

const PerformancePanel = () => (
  <div className="space-y-3 sm:space-y-4">
    <div className="grid grid-cols-3 gap-2">
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-2.5 sm:p-3">
        <p className="text-[10px] text-gray-500 sm:text-xs">Views</p>
        <p className="mt-0.5 text-base font-bold text-gray-900 sm:text-lg">128K</p>
        <p className="text-[10px] font-semibold text-emerald-600 sm:text-xs">▲ 18%</p>
      </div>
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-2.5 sm:p-3">
        <p className="text-[10px] text-gray-500 sm:text-xs">Clicks</p>
        <p className="mt-0.5 text-base font-bold text-gray-900 sm:text-lg">4.7K</p>
        <p className="text-[10px] font-semibold text-emerald-600 sm:text-xs">▲ 9%</p>
      </div>
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-2.5 sm:p-3">
        <p className="text-[10px] text-gray-500 sm:text-xs">Sales</p>
        <p className="mt-0.5 text-base font-bold text-gray-900 sm:text-lg">312</p>
        <p className="text-[10px] font-semibold text-emerald-600 sm:text-xs">▲ 27%</p>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-xl border border-orange-100 bg-orange-50 px-3 py-2.5">
        <p className="text-[10px] text-gray-500 sm:text-xs">ROAS</p>
        <p className="text-base font-bold text-orange-600 sm:text-lg">4.2x</p>
      </div>
      <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5">
        <p className="text-[10px] text-gray-500 sm:text-xs">Engagement</p>
        <p className="text-base font-bold text-blue-600 sm:text-lg">6.8%</p>
      </div>
    </div>

    <div className="rounded-xl border border-gray-100 bg-white p-3 sm:p-3.5">
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>Campaign lift</span>
        <span className="font-semibold text-orange-600">+24%</span>
      </div>
      <div className="mt-2.5 grid grid-cols-12 gap-1">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className="h-7 rounded-md bg-gradient-to-t from-orange-100 to-orange-400/70 sm:h-8"
            style={{ opacity: 0.45 + (i % 4) * 0.15 }}
          />
        ))}
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="text-[10px] text-gray-500 sm:text-xs">Auto-optimized budget</span>
        <span className="rounded-full bg-orange-500 px-2.5 py-1 text-[10px] font-bold text-white sm:text-xs">
          Boost
        </span>
      </div>
    </div>
  </div>
);

const EarnPanel = () => (
  <div className="space-y-3 sm:space-y-4">
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
        <p className="text-[10px] text-gray-500 sm:text-xs">Creator</p>
        <div className="mt-2 grid h-11 place-items-center rounded-lg bg-white ring-1 ring-gray-100 sm:h-12">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-orange-50 ring-1 ring-orange-100">
            <FiPlay className="h-3.5 w-3.5 text-orange-500" />
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[10px] text-gray-500 sm:text-xs">Reel draft</span>
          <span className="text-[10px] font-semibold text-orange-600 sm:text-xs">00:18</span>
        </div>
      </div>
      <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-3">
        <p className="text-[10px] text-gray-500 sm:text-xs">Brand offer</p>
        <div className="mt-2 grid h-11 place-items-center rounded-lg bg-white ring-1 ring-orange-100 sm:h-12">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-50 ring-1 ring-blue-100">
            <FiArrowUpRight className="h-3.5 w-3.5 text-blue-500" />
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[10px] text-gray-500 sm:text-xs">Payout</span>
          <span className="text-[10px] font-semibold text-orange-600 sm:text-xs">₹12K</span>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-2">
      <Metric label="Reach" value="48K" />
      <Metric label="Eng. rate" value="7.2%" tone="orange" />
      <Metric label="Est. earn" value="₹8K" tone="green" />
    </div>

    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-900 sm:text-sm">Match in minutes</p>
        <p className="text-[10px] text-gray-500 sm:text-xs">Chat, approve, and launch fast</p>
      </div>
      <span className="shrink-0 rounded-full bg-orange-500 px-3 py-1.5 text-[10px] font-bold text-white sm:text-xs">
        Go Live
      </span>
    </div>
  </div>
);

const PANEL_MAP = {
  chat: ChatPanel,
  performance: PerformancePanel,
  earn: EarnPanel,
};

const ShowcaseMockup = () => {
  const [active, setActive] = useState(0);
  const slide = SLIDES[active];
  const Panel = PANEL_MAP[slide.id];

  useEffect(() => {
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) return undefined;

    const id = window.setInterval(() => {
      setActive((prev) => (prev + 1) % SLIDES.length);
    }, AUTO_ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="relative min-w-0 w-full">
      <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-orange-500/25 via-transparent to-blue-500/20 blur-2xl sm:-inset-4" />

      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
        {/* Mock chrome — same idea as Insta Tools info mockups */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-orange-500 text-white">
              {slide.id === "chat" ? (
                <FiMessageCircle className="h-4 w-4" />
              ) : slide.id === "performance" ? (
                <FiArrowUpRight className="h-4 w-4" />
              ) : (
                <FiPlay className="h-4 w-4" />
              )}
            </span>
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-gray-900 sm:text-sm">{slide.title}</p>
              <p className="truncate text-[10px] text-gray-500 sm:text-xs">{slide.hint}</p>
            </div>
          </div>
          <SocialIconRow className="shrink-0" />
        </div>

        <div className="flex gap-1.5 overflow-x-auto border-b border-gray-100 bg-white px-2 py-2 [scrollbar-width:none] [-ms-overflow-style:none] sm:gap-2 sm:px-3">
          {SLIDES.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(i)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-semibold transition sm:text-xs ${
                i === active
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="bg-white p-3 sm:p-4 md:p-5">
          <Panel />
        </div>

        <div className="flex items-center justify-center gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2.5">
          {SLIDES.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`Show ${item.label}`}
              onClick={() => setActive(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === active ? "w-6 bg-orange-500" : "w-2 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function HomepageSocialShowcase() {
  return (
    <section className="relative overflow-hidden bg-black py-16 px-4 sm:px-6 md:py-24 lg:px-8">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-10 xl:gap-12">
        {/* Text — Insta Tools info style */}
        <div className="flex flex-col justify-center lg:pr-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-orange-400 sm:text-xs">
            Social-first campaigns
          </p>
          <h2 className="mt-3 text-xl font-bold leading-tight text-white sm:text-2xl md:text-3xl lg:text-[2rem]">
            Where creators and brands collaborate—fast.
          </h2>
          <p className="mt-3 max-w-xl text-xs leading-relaxed text-white/70 sm:mt-4 sm:text-sm md:text-[15px]">
            Infuber helps influencers earn with reels and stories, while brand managers find the right
            creators, approve content, and track results across Instagram, TikTok, YouTube, and Facebook.
          </p>

          <ul className="mt-5 space-y-2.5 sm:mt-6 sm:space-y-3">
            {BULLETS.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2.5 text-xs text-white/80 sm:gap-3 sm:text-sm md:text-[15px]"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap">
            <Link
              to="/marketplace"
              className="inline-flex items-center justify-center rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-black transition hover:bg-orange-600 sm:px-6 sm:py-3"
            >
              Explore Marketplace
            </Link>
            <Link
              to="/our-work"
              className="inline-flex items-center justify-center rounded-xl border border-white/80 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white hover:text-black sm:px-6 sm:py-3"
            >
              See How It Works
            </Link>
          </div>
        </div>

        {/* Visual — full-width framed mockup, no clipped content */}
        <div className="relative min-w-0 w-full">
          <ShowcaseMockup />
        </div>
      </div>
    </section>
  );
}
